import React, { useState } from 'react';
import {
  Breadcrumbs,
  Link,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useCategoryContext } from '../contexts/CategoryContext';
import { Category } from '../types/bookmarks';

/**
 * Maximum total segments to display before collapsing intermediate ones.
 * When total path segments exceed this threshold, show first segment,
 * an ellipsis button, then the last 2 segments.
 */
const MAX_VISIBLE_SEGMENTS = 4;

/**
 * BreadcrumbsBar: Displays the navigation path from root to the selected category.
 *
 * - No category selected: shows "Bookmarks" as plain text
 * - Category selected: "Bookmarks" (clickable) > ...ancestors... > selected (plain text)
 * - Overflow: collapses intermediate segments into an ellipsis with a dropdown menu
 *
 * Positioned within ContentHeader with flex:1 and overflow:hidden so that
 * action buttons on the right are never pushed off-screen.
 *
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5, 10.6, 10.7, 10.8, 12.9
 */
const BreadcrumbsBar: React.FC = () => {
  const {
    selectedCategory,
    ancestors,
    selectCategory,
    navigateToAncestor,
    activeApp,
    selectedNote,
    selectedNoteCategory,
    noteAncestors,
    selectNoteCategory,
    notesTrashOpen,
  } = useCategoryContext();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Notes app: render a dedicated (non-collapsing) breadcrumb.
  if (activeApp === 'notes') {
    if (notesTrashOpen) {
      return (
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': { mx: '8px' },
              fontSize: { xs: '16px', md: '14px' },
            }}
          >
            <Typography color="text.primary" sx={{ fontSize: 'inherit' }}>
              Notes
            </Typography>
            <Typography color="text.primary" sx={{ fontSize: 'inherit' }}>
              Trash
            </Typography>
          </Breadcrumbs>
        </Box>
      );
    }

    // Path: Notes (root) → note ancestors → [selected category] → [note title]
    const midCategories = selectedNote
      ? noteAncestors // for a note, noteAncestors already includes its parent
      : [...noteAncestors];
    const lastLabel = selectedNote
      ? selectedNote.note_title
      : selectedNoteCategory?.category_name ?? null;

    return (
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{
            '& .MuiBreadcrumbs-separator': { mx: '8px' },
            fontSize: { xs: '16px', md: '14px' },
          }}
        >
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => selectNoteCategory(null)}
            sx={{ fontSize: 'inherit', cursor: 'pointer' }}
          >
            Notes
          </Link>
          {midCategories.map((cat) => (
            <Link
              key={cat.category_id}
              component="button"
              underline="hover"
              color="inherit"
              onClick={() => selectNoteCategory(cat)}
              sx={{ fontSize: 'inherit', cursor: 'pointer' }}
            >
              {cat.category_name}
            </Link>
          ))}
          {lastLabel && (
            <Typography color="text.primary" sx={{ fontSize: 'inherit' }}>
              {lastLabel}
            </Typography>
          )}
        </Breadcrumbs>
      </Box>
    );
  }

  const handleEllipsisClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleNavigateToRoot = () => {
    selectCategory(null);
  };

  const handleNavigateToAncestor = (category: Category) => {
    navigateToAncestor(category);
    handleMenuClose();
  };

  // No category selected: show only "Bookmarks" as plain text
  if (!selectedCategory) {
    return (
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{
            '& .MuiBreadcrumbs-separator': { mx: '8px' },
            fontSize: { xs: '16px', md: '14px' },
          }}
        >
          <Typography color="text.primary" sx={{ fontSize: 'inherit' }}>
            Bookmarks
          </Typography>
        </Breadcrumbs>
      </Box>
    );
  }

  // Build the full segments list: [root, ...ancestors, selectedCategory]
  // "Bookmarks" is always the first segment (clickable when a category is selected)
  // Ancestors are clickable middle segments
  // Selected category is the last segment (plain text)
  const allSegments: Array<{ label: string; category: Category | null }> = [
    { label: 'Bookmarks', category: null }, // root
    ...ancestors.map((a) => ({ label: a.category_name, category: a })),
    { label: selectedCategory.category_name, category: selectedCategory },
  ];

  const totalSegments = allSegments.length;
  const needsCollapse = totalSegments > MAX_VISIBLE_SEGMENTS;

  // Determine which segments to show and which to hide
  let visibleSegments: Array<{
    label: string;
    category: Category | null;
    isLast: boolean;
  }>;
  let hiddenSegments: Array<{ label: string; category: Category | null }> = [];

  if (needsCollapse) {
    // Show first segment + ellipsis + last 2 segments
    const firstSegment = allSegments[0];
    const lastTwo = allSegments.slice(-2);
    hiddenSegments = allSegments.slice(1, -2);

    visibleSegments = [
      { ...firstSegment, isLast: false },
      // Ellipsis placeholder handled separately in render
      ...lastTwo.map((seg, idx) => ({
        ...seg,
        isLast: idx === lastTwo.length - 1,
      })),
    ];
  } else {
    visibleSegments = allSegments.map((seg, idx) => ({
      ...seg,
      isLast: idx === totalSegments - 1,
    }));
  }

  return (
    <Box sx={{ flex: 1, overflow: 'hidden' }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-separator': { mx: '8px' },
          fontSize: { xs: '16px', md: '14px' },
        }}
      >
        {/* First segment: always "Bookmarks", clickable */}
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={handleNavigateToRoot}
          sx={{ fontSize: 'inherit', cursor: 'pointer' }}
        >
          Bookmarks
        </Link>

        {/* Ellipsis button for collapsed segments */}
        {needsCollapse && (
          <IconButton
            size="small"
            onClick={handleEllipsisClick}
            aria-label="Show hidden breadcrumb segments"
            sx={{ p: 0 }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        )}

        {/* Remaining visible segments (after first, which is rendered above) */}
        {(needsCollapse
          ? visibleSegments.slice(1)
          : visibleSegments.slice(1)
        ).map((seg) => {
          if (seg.isLast) {
            // Last segment: plain text, not clickable
            return (
              <Typography
                key={seg.label + '-last'}
                color="text.primary"
                sx={{ fontSize: 'inherit' }}
              >
                {seg.label}
              </Typography>
            );
          }

          // Middle ancestor segments: clickable
          return (
            <Link
              key={seg.category?.category_id || seg.label}
              component="button"
              underline="hover"
              color="inherit"
              onClick={() => {
                if (seg.category) {
                  handleNavigateToAncestor(seg.category);
                } else {
                  handleNavigateToRoot();
                }
              }}
              sx={{ fontSize: 'inherit', cursor: 'pointer' }}
            >
              {seg.label}
            </Link>
          );
        })}
      </Breadcrumbs>

      {/* Dropdown menu for hidden ancestors */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {hiddenSegments.map((seg) => (
          <MenuItem
            key={seg.category?.category_id || seg.label}
            onClick={() => {
              if (seg.category) {
                handleNavigateToAncestor(seg.category);
              } else {
                handleNavigateToRoot();
              }
            }}
          >
            {seg.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default BreadcrumbsBar;
